import React, { useState, useEffect, useRef, useCallback } from "react";
import { formService } from "../../_services/form.service";
import "./FormFill.css";

// Answers for the 'Your details' step on a generic share link (not a template section).
const RESPONDENT = '__respondent';

/**
 * FormFill - Public client-facing form page (no auth required).
 * Clients receive a share link like /forms/fill/:formId and can
 * fill in the form without logging in, similar to Fresha.
 */
const FormFill = (props) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [signatureData, setSignatureData] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Typeform-style navigation state
  const [questionItems, setQuestionItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationDirection, setAnimationDirection] = useState('forward');
  const [isAnimating, setIsAnimating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const canvasRef = useRef(null);
  const lastPointRef = useRef(null);
  const inputRef = useRef(null);

  const formId = props.match?.params?.formId;

  // Parse tracking query params from URL
  const searchParams = new URLSearchParams(props.location?.search || "");
  const submissionId = searchParams.get("sid");
  const clientId = searchParams.get("client");
  const bookingId = searchParams.get("booking");

  useEffect(() => {
    if (formId) {
      loadTemplate();
    } else {
      setError("Invalid form link");
      setLoading(false);
    }
  }, [formId]);

  const loadTemplate = () => {
    setLoading(true);
    formService.publicViewForm({ formId })
      .then((response) => {
        const data = response?.data?.responseData;
        if (data?.result) {
          setTemplate(data.result);
        } else {
          setError("This form is no longer available");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("This form is no longer available");
        setLoading(false);
      });
  };

  // Flatten nested sections/questions into linear array for Typeform-style display
  const flattenQuestions = (template) => {
    const items = [];
    let globalIndex = 0;

    // 1. Intro screen
    items.push({
      id: 'intro',
      type: 'intro',
      globalIndex: globalIndex++,
      formName: template.name,
      formDescription: template.description || "Please complete all required fields below",
      heroImage: template.heroImage
    });

    // A generic share link has no client attached, so ask who they are —
    // unless the form already collects a name and a way to reach them.
    if (!submissionId) {
      const clientFields = (template.sections || [])
        .filter((sec) => sec.type === 'client_details' && sec.fields)
        .flatMap((sec) => sec.fields.filter((f) => f.enabled).map((f) => f.id));
      const hasName = clientFields.some((id) => ['firstName', 'lastName', 'name', 'fullName'].includes(id));
      const hasContact = clientFields.some((id) => ['email', 'mobileNumber', 'phone', 'phoneNumber'].includes(id));
      if (!(hasName && hasContact)) {
        [
          { id: 'name', label: 'Your name', required: true, inputType: 'text' },
          { id: 'email', label: 'Your email', required: true, inputType: 'email' },
          { id: 'phone', label: 'Your mobile number', required: false, inputType: 'tel' },
        ].forEach((f) => {
          items.push({
            id: `${RESPONDENT}_${f.id}`,
            type: 'client_field',
            sectionId: RESPONDENT,
            questionId: f.id,
            question: f.label,
            required: f.required,
            inputType: f.inputType,
            placeholder: f.label,
            answerKey: `${RESPONDENT}_${f.id}`,
            globalIndex: globalIndex++,
          });
        });
      }
    }

    // 2. Process sections
    if (template.sections) {
      template.sections.forEach((section) => {
      // Client detail fields
      if (section.type === 'client_details' && section.fields) {
        section.fields
          .filter(f => f.enabled)
          .forEach((field, idx) => {
            items.push({
              id: `${section.id}_${field.id}`,
              type: 'client_field',
              sectionId: section.id,
              questionId: field.id,
              question: field.label,
              required: field.required,
              inputType: field.id === 'email' ? 'email' : field.id === 'mobileNumber' ? 'tel' : field.id === 'birthday' ? 'date' : 'text',
              placeholder: field.label,
              answerKey: `${section.id}_${field.id}`,
              isTextarea: field.id === 'address',
              globalIndex: globalIndex++,
              metadata: {
                sectionTitle: section.title,
                sectionDescription: section.description,
                totalInSection: section.fields.filter(f => f.enabled).length,
                indexInSection: idx
              }
            });
          });
      }

      // Custom questions
      if (section.questions) {
        section.questions.forEach((question, idx) => {
          items.push({
            id: `${section.id}_${question.id}`,
            type: question.type,
            sectionId: section.id,
            questionId: question.id,
            question: question.question,
            required: question.required,
            options: question.options || [],
            answerKey: `${section.id}_${question.id}`,
            globalIndex: globalIndex++,
            metadata: {
              sectionTitle: section.title,
              sectionDescription: section.description,
              totalInSection: section.questions.length,
              indexInSection: idx
            }
          });
        });
      }
    });
    }

    // 3. Signature (if required)
    if (template.settings?.requireSignature) {
      items.push({
        id: 'signature',
        type: 'signature',
        question: 'Please sign below to confirm your responses',
        required: true,
        globalIndex: globalIndex++
      });
    }

    // 4. Submit screen
    items.push({
      id: 'submit',
      type: 'submit',
      globalIndex: globalIndex++
    });

    return items;
  };

  // Flatten questions when template loads
  useEffect(() => {
    if (template) {
      const flattened = flattenQuestions(template);
      setQuestionItems(flattened);
    }
  }, [template]);

  const setAnswer = (key, value) => {
    setValidationError(null);
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCheckboxAnswer = (key, option) => {
    setValidationError(null);
    setAnswers((prev) => {
      const current = prev[key] || [];
      if (current.includes(option)) {
        return { ...prev, [key]: current.filter((o) => o !== option) };
      }
      return { ...prev, [key]: [...current, option] };
    });
  };

  // ===== Typeform Navigation =====
  const validateCurrentQuestion = (item) => {
    if (!item || !item.required) return true;

    const answer = item.type === 'signature'
      ? signatureData
      : answers[item.answerKey];

    if (!answer || answer === '' || (Array.isArray(answer) && answer.length === 0)) {
      setValidationError('Please answer this question to continue');
      return false;
    }

    return true;
  };

  const goToNext = () => {
    if (isAnimating) return;

    const currentItem = questionItems[currentIndex];

    // Validate current question before advancing (except intro and submit)
    if (currentItem && currentItem.type !== 'intro' && currentItem.type !== 'submit') {
      if (!validateCurrentQuestion(currentItem)) {
        return;
      }
    }

    if (currentIndex < questionItems.length - 1) {
      setAnimationDirection('forward');
      setIsAnimating(true);
      setCurrentIndex(currentIndex + 1);

      setTimeout(() => setIsAnimating(false), 400);
    }
  };

  const goToPrevious = () => {
    if (isAnimating) return;
    setValidationError(null);

    if (currentIndex > 0) {
      setAnimationDirection('backward');
      setIsAnimating(true);
      setCurrentIndex(currentIndex - 1);

      setTimeout(() => setIsAnimating(false), 400);
    }
  };

  // Auto-focus input when question changes
  useEffect(() => {
    if (inputRef.current && !isAnimating) {
      const timer = setTimeout(() => {
        inputRef.current.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, isAnimating]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      const currentItem = questionItems[currentIndex];

      // Enter key: advance to next question
      if (e.key === 'Enter' && !e.shiftKey) {
        // Don't trigger on textareas unless Ctrl/Cmd+Enter
        if (currentItem?.isTextarea || currentItem?.type === 'long_answer') {
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            goToNext();
          }
        } else {
          e.preventDefault();
          goToNext();
        }
      }

      // Arrow Up: go to previous question
      if (e.key === 'ArrowUp' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        goToPrevious();
      }

      // Arrow Down: go to next question
      if (e.key === 'ArrowDown' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, questionItems, answers, signatureData, isAnimating]);

  // ===== Signature Pad =====
  const requireSignature = template?.settings?.requireSignature === true;

  const getCanvasPoint = useCallback((e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDrawing = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = getCanvasPoint(e, canvas);
    lastPointRef.current = point;
    setIsDrawing(true);
  }, [getCanvasPoint]);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const point = getCanvasPoint(e, canvas);
    const last = lastPointRef.current;
    if (last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(point.x, point.y);
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
    }
    lastPointRef.current = point;
  }, [isDrawing, getCanvasPoint]);

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL("image/png"));
    }
  }, [isDrawing]);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setSignatureData(null);
  }, []);

  const handleSubmit = () => {
    if (!template) return;

    // Validate all required questions using the flattened question list
    const firstMissing = questionItems.find(item => {
      if (!item.required) return false;
      if (item.type === 'intro' || item.type === 'submit') return false;
      if (item.type === 'signature') return !signatureData;
      const answer = answers[item.answerKey];
      return !answer || answer === '' || (Array.isArray(answer) && answer.length === 0);
    });

    if (firstMissing) {
      const idx = questionItems.indexOf(firstMissing);
      if (idx !== -1) {
        setAnimationDirection('backward');
        setCurrentIndex(idx);
      }
      setValidationError('Please answer all required questions before submitting');
      return;
    }

    if (requireSignature && !signatureData) {
      const sigIdx = questionItems.findIndex(i => i.type === 'signature');
      if (sigIdx !== -1) {
        setAnimationDirection('backward');
        setCurrentIndex(sigIdx);
      }
      setValidationError('Please provide your signature');
      return;
    }

    // Build responses
    const responses = [];
    template.sections.forEach((section) => {
      if (section.type === "client_details" && section.fields) {
        section.fields
          .filter((f) => f.enabled)
          .forEach((field) => {
            const key = `${section.id}_${field.id}`;
            if (answers[key] !== undefined && answers[key] !== "") {
              responses.push({
                sectionId: section.id,
                questionId: field.id,
                questionText: field.label,
                questionType: "client_field",
                answer: answers[key],
              });
            }
          });
      }
      if (section.questions) {
        section.questions.forEach((question) => {
          const key = `${section.id}_${question.id}`;
          if (answers[key] !== undefined && answers[key] !== "") {
            responses.push({
              sectionId: section.id,
              questionId: question.id,
              questionText: question.question,
              questionType: question.type,
              answer: answers[key],
            });
          }
        });
      }
    });

    setSubmitting(true);
    const submitPayload = {
      formTemplateId: template._id,
      responses,
      signature: requireSignature ? signatureData : null,
    };
    if (submissionId) submitPayload.submissionId = submissionId;
    else {
      const who = {
        name: answers[`${RESPONDENT}_name`],
        email: answers[`${RESPONDENT}_email`],
        phone: answers[`${RESPONDENT}_phone`],
      };
      if (who.name || who.email || who.phone) submitPayload.respondent = who;
    }
    if (clientId) submitPayload.clientId = clientId;
    if (bookingId) submitPayload.bookingId = bookingId;

    formService.publicSubmitForm(submitPayload)
    .then(() => {
      setSubmitted(true);
      setSubmitting(false);
    })
    .catch(() => {
      setValidationError('Failed to submit form. Please try again.');
      setSubmitting(false);
    });
  };

  // ===== Rendering Functions =====
  const renderIntro = (item) => (
    <div className="form-intro">
      {item.heroImage && (
        <div className="form-hero">
          <img src={item.heroImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#f5f5f5' }} />
        </div>
      )}
      <h1>{item.formName || 'Form'}</h1>
      <p>{item.formDescription}</p>
    </div>
  );

  const renderShortAnswer = (item) => (
    <>
      <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '24px', color: '#1a1a1a' }}>
        {item.question}
        {item.required && <span style={{ color: '#e53935' }}> *</span>}
      </h2>
      <input
        ref={inputRef}
        type={item.inputType || 'text'}
        value={answers[item.answerKey] || ''}
        onChange={(e) => setAnswer(item.answerKey, e.target.value)}
        placeholder="Type your answer here..."
        style={{
          width: '100%',
          padding: '14px 18px',
          border: '2px solid #e0e0e0',
          borderRadius: '10px',
          fontSize: '16px',
          outline: 'none',
          transition: 'border-color 0.2s ease',
          boxSizing: 'border-box'
        }}
        onFocus={(e) => e.target.style.borderColor = '#1a1a1a'}
        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
      />
    </>
  );

  const renderLongAnswer = (item) => (
    <>
      <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '24px', color: '#1a1a1a' }}>
        {item.question}
        {item.required && <span style={{ color: '#e53935' }}> *</span>}
      </h2>
      <textarea
        ref={inputRef}
        value={answers[item.answerKey] || ''}
        onChange={(e) => setAnswer(item.answerKey, e.target.value)}
        placeholder="Type your answer here..."
        rows={6}
        style={{
          width: '100%',
          padding: '14px 18px',
          border: '2px solid #e0e0e0',
          borderRadius: '10px',
          fontSize: '16px',
          outline: 'none',
          resize: 'vertical',
          fontFamily: 'inherit',
          transition: 'border-color 0.2s ease',
          boxSizing: 'border-box'
        }}
        onFocus={(e) => e.target.style.borderColor = '#1a1a1a'}
        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
      />
      <p style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>
        Press Ctrl/Cmd + Enter to continue
      </p>
    </>
  );

  const renderMultipleChoice = (item) => (
    <>
      <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '32px', color: '#1a1a1a' }}>
        {item.question}
        {item.required && <span style={{ color: '#e53935' }}> *</span>}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {item.options.map((option, idx) => (
          <button
            key={idx}
            ref={idx === 0 ? inputRef : null}
            onClick={() => {
              setAnswer(item.answerKey, option);
              // Auto-advance after selection
              setTimeout(() => goToNext(), 300);
            }}
            style={{
              padding: '16px 20px',
              border: answers[item.answerKey] === option ? '2px solid #1a1a1a' : '2px solid #e8e8e8',
              borderRadius: '12px',
              background: answers[item.answerKey] === option ? '#fafafa' : '#fff',
              fontSize: '16px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: answers[item.answerKey] === option ? '500' : '400',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1a1a1a';
            }}
            onMouseLeave={(e) => {
              if (answers[item.answerKey] !== option) {
                e.currentTarget.style.borderColor = '#e8e8e8';
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: '2px solid',
                borderColor: answers[item.answerKey] === option ? '#1a1a1a' : '#d0d0d0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {answers[item.answerKey] === option && (
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: '#1a1a1a',
                  }} />
                )}
              </div>
              <span>{option}</span>
            </div>
          </button>
        ))}
      </div>
    </>
  );

  const renderCheckbox = (item) => (
    <>
      <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '32px', color: '#1a1a1a' }}>
        {item.question}
        {item.required && <span style={{ color: '#e53935' }}> *</span>}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {item.options.map((option, idx) => {
          const isChecked = (answers[item.answerKey] || []).includes(option);
          return (
            <button
              key={idx}
              ref={idx === 0 ? inputRef : null}
              onClick={() => toggleCheckboxAnswer(item.answerKey, option)}
              style={{
                padding: '16px 20px',
                border: isChecked ? '2px solid #1a1a1a' : '2px solid #e8e8e8',
                borderRadius: '12px',
                background: isChecked ? '#fafafa' : '#fff',
                fontSize: '16px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontWeight: isChecked ? '500' : '400',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#1a1a1a';
              }}
              onMouseLeave={(e) => {
                if (!isChecked) {
                  e.currentTarget.style.borderColor = '#e8e8e8';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  border: '2px solid',
                  borderColor: isChecked ? '#1a1a1a' : '#d0d0d0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isChecked ? '#1a1a1a' : 'transparent',
                }}>
                  {isChecked && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span>{option}</span>
              </div>
            </button>
          );
        })}
      </div>
      <p style={{ fontSize: '13px', color: '#888', marginTop: '16px' }}>
        Select all that apply
      </p>
    </>
  );

  const renderDropdown = (item) => (
    <>
      <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '24px', color: '#1a1a1a' }}>
        {item.question}
        {item.required && <span style={{ color: '#e53935' }}> *</span>}
      </h2>
      <select
        ref={inputRef}
        value={answers[item.answerKey] || ''}
        onChange={(e) => setAnswer(item.answerKey, e.target.value)}
        style={{
          width: '100%',
          padding: '14px 18px',
          border: '2px solid #e0e0e0',
          borderRadius: '10px',
          fontSize: '16px',
          outline: 'none',
          transition: 'border-color 0.2s ease',
          background: '#fff',
          cursor: 'pointer',
          boxSizing: 'border-box'
        }}
        onFocus={(e) => e.target.style.borderColor = '#1a1a1a'}
        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
      >
        <option value="">Select an option</option>
        {item.options.map((option, idx) => (
          <option key={idx} value={option}>{option}</option>
        ))}
      </select>
    </>
  );

  const renderYesNo = (item) => (
    <>
      <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '32px', color: '#1a1a1a' }}>
        {item.question}
        {item.required && <span style={{ color: '#e53935' }}> *</span>}
      </h2>
      <div style={{ display: 'flex', gap: '16px' }}>
        {['Yes', 'No'].map((option) => (
          <button
            key={option}
            ref={option === 'Yes' ? inputRef : null}
            onClick={() => {
              setAnswer(item.answerKey, option);
              // Auto-advance after selection
              setTimeout(() => goToNext(), 300);
            }}
            style={{
              flex: 1,
              padding: '20px',
              border: answers[item.answerKey] === option ? '2px solid #1a1a1a' : '2px solid #e8e8e8',
              borderRadius: '12px',
              background: answers[item.answerKey] === option ? '#1a1a1a' : '#fff',
              color: answers[item.answerKey] === option ? '#fff' : '#1a1a1a',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {option}
          </button>
        ))}
      </div>
    </>
  );

  const renderSignature = (item) => (
    <>
      <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>
        {item.question}
        {item.required && <span style={{ color: '#e53935' }}> *</span>}
      </h2>
      <p style={{ fontSize: '15px', color: '#666', marginBottom: '24px' }}>
        Draw your signature in the box below
      </p>
      <div style={{
        position: 'relative',
        border: signatureData ? '2px solid #1a1a1a' : '2px dashed #d0d0d0',
        borderRadius: '12px',
        overflow: 'hidden',
        background: '#fafafa',
      }}>
        <canvas
          ref={canvasRef}
          width={560}
          height={200}
          style={{
            width: '100%',
            height: '200px',
            display: 'block',
            cursor: 'crosshair',
            touchAction: 'none',
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!signatureData && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#bbb',
            fontSize: '14px',
            pointerEvents: 'none',
          }}>
            Sign here
          </div>
        )}
      </div>
      {signatureData && (
        <button
          type="button"
          onClick={clearSignature}
          style={{
            marginTop: '12px',
            background: 'none',
            border: 'none',
            color: '#888',
            fontSize: '13px',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: 0,
          }}
        >
          Clear signature
        </button>
      )}
    </>
  );

  const getDisplayAnswer = (item) => {
    if (item.type === 'signature') {
      return signatureData ? '✓ Signed' : null;
    }
    const val = answers[item.answerKey];
    if (Array.isArray(val)) return val.length > 0 ? val.join(', ') : null;
    return val || null;
  };

  const renderSubmitScreen = () => {
    const answeredItems = questionItems.filter(item =>
      item.type !== 'intro' && item.type !== 'submit' && item.question
    );

    return (
      <div style={{ padding: '24px 0' }}>
        <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '8px', color: '#1a1a1a', textAlign: 'center' }}>
          Review your answers
        </h2>
        <p style={{ fontSize: '15px', color: '#888', marginBottom: '28px', textAlign: 'center' }}>
          Tap any answer to edit it
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
          {answeredItems.map((item) => {
            const display = getDisplayAnswer(item);
            const idx = questionItems.indexOf(item);
            return (
              <div
                key={item.id}
                onClick={() => {
                  if (idx !== -1) {
                    setAnimationDirection('backward');
                    setCurrentIndex(idx);
                  }
                }}
                style={{
                  padding: '14px 18px',
                  borderRadius: '10px',
                  border: '1px solid #e8e8e8',
                  background: '#fafafa',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ccc';
                  e.currentTarget.style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e8e8e8';
                  e.currentTarget.style.background = '#fafafa';
                }}
              >
                <div style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>
                  {item.question}
                  {item.required && <span style={{ color: '#e53935' }}> *</span>}
                </div>
                <div style={{
                  fontSize: '15px',
                  color: display ? '#1a1a1a' : '#ccc',
                  fontWeight: display ? '500' : '400',
                  fontStyle: display ? 'normal' : 'italic',
                }}>
                  {display || 'Not answered'}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '16px 48px',
              borderRadius: '12px',
              border: 'none',
              background: submitting ? '#999' : '#1a1a1a',
              color: '#fff',
              fontSize: '18px',
              fontWeight: '600',
              cursor: submitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              width: '100%',
              maxWidth: '320px',
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Form'}
          </button>
        </div>
      </div>
    );
  };

  const renderQuestionByType = (item) => {
    switch (item.type) {
      case 'intro':
        return renderIntro(item);
      case 'client_field':
        return item.isTextarea ? renderLongAnswer(item) : renderShortAnswer(item);
      case 'short_answer':
        return renderShortAnswer(item);
      case 'long_answer':
        return renderLongAnswer(item);
      case 'multiple_choice':
        return renderMultipleChoice(item);
      case 'checkbox':
        return renderCheckbox(item);
      case 'dropdown':
        return renderDropdown(item);
      case 'yes_no':
        return renderYesNo(item);
      case 'signature':
        return renderSignature(item);
      case 'submit':
        return renderSubmitScreen();
      default:
        return null;
    }
  };

  const renderCurrentQuestion = () => {
    if (submitted) {
      return (
        <div style={{ ...cardStyle, padding: "60px 32px", textAlign: "center" }}>
          <div style={{
            width: "64px", height: "64px", borderRadius: "50%",
            background: "#e6f9f0", display: "flex", alignItems: "center",
            justifyContent: "center", margin: "0 auto 20px",
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2e7d5b" strokeWidth="2">
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 style={{ fontSize: "22px", fontWeight: "600", margin: "0 0 8px", color: "#1a1a1a" }}>
            Thank you!
          </h2>
          <p style={{ fontSize: "15px", color: "#888", margin: 0, lineHeight: 1.5 }}>
            Your form has been submitted successfully.
          </p>
          {(() => {
            const params = new URLSearchParams(window.location.search);
            const returnUrl = params.get('returnUrl');
            if (returnUrl) {
              return (
                <button
                  onClick={() => { if (props.history) props.history.push(returnUrl); else window.location.href = returnUrl; }}
                  style={{
                    marginTop: "24px", padding: "12px 32px", border: "none",
                    background: "#29282D", color: "#fff", borderRadius: "10px",
                    fontSize: "15px", fontWeight: "600", cursor: "pointer",
                  }}
                >
                  Done
                </button>
              );
            }
            return <p style={{ fontSize: "14px", color: "#bbb", margin: "16px 0 0" }}>You can close this page.</p>;
          })()}
        </div>
      );
    }

    if (loading) {
      return (
        <div style={{ ...cardStyle, padding: "60px 32px", textAlign: "center" }}>
          <p style={{ color: "#888", fontSize: "15px" }}>Loading form...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ ...cardStyle, padding: "60px 32px", textAlign: "center" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" style={{ marginBottom: "16px" }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <p style={{ color: "#888", fontSize: "15px" }}>{error}</p>
        </div>
      );
    }

    const currentItem = questionItems[currentIndex];
    if (!currentItem) return null;

    const animationClass = animationDirection === 'forward'
      ? 'slide-forward'
      : 'slide-backward';

    return (
      <div key={currentItem.id} className={`question-container ${animationClass}`}>
        <div className="question-content">
          {renderQuestionByType(currentItem)}
        </div>
      </div>
    );
  };

  const renderNavigationButtons = () => {
    const currentItem = questionItems[currentIndex];
    const isIntro = currentItem?.type === 'intro';
    const isSubmit = currentItem?.type === 'submit';

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '24px 0',
        gap: '16px',
        position: 'relative',
      }}>
        {/* Back button */}
        {currentIndex > 0 && (
          <button
            onClick={goToPrevious}
            disabled={isAnimating}
            style={{
              padding: '12px 24px',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              background: '#fff',
              color: '#1a1a1a',
              fontSize: '15px',
              fontWeight: '500',
              cursor: isAnimating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isAnimating ? 0.5 : 1,
              transition: 'all 0.2s ease',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Next/OK button */}
        {!isSubmit && (
          <button
            onClick={goToNext}
            disabled={isAnimating}
            style={{
              padding: '12px 32px',
              border: 'none',
              borderRadius: '10px',
              background: '#1a1a1a',
              color: '#fff',
              fontSize: '15px',
              fontWeight: '500',
              cursor: isAnimating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isAnimating ? 0.5 : 1,
              transition: 'all 0.2s ease',
            }}
          >
            {isIntro ? 'Start' : 'OK'}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Question counter */}
        {questionItems.length > 0 && (
          <div style={{
            fontSize: '13px',
            color: '#888',
            position: 'absolute',
            right: '0',
            bottom: '-24px'
          }}>
            {currentIndex + 1} / {questionItems.length}
          </div>
        )}
      </div>
    );
  };

  // ===== Render =====
  const cardStyle = {
    background: "#fff",
    borderRadius: "12px",
    border: "1px solid #e8e8e8",
    marginBottom: "16px",
    overflow: "hidden",
  };

  return (
    <div className="form-fill-page">
      <div className="form-fill-container">
        {/* Pinned Hero Image Header (after intro) */}
        {!submitted && !loading && !error && currentIndex > 0 && template?.heroImage && (
          <div style={{
            width: '100%',
            height: '120px',
            borderRadius: '12px',
            overflow: 'hidden',
            marginBottom: '16px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          }}>
            <img
              src={template.heroImage}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                background: '#f5f5f5',
                display: 'block',
              }}
            />
          </div>
        )}

        {/* Progress Bar */}
        {!submitted && !loading && !error && currentIndex > 0 && questionItems.length > 0 && (
          <div style={{
            width: '100%',
            background: '#e5e5e5',
            height: '3px',
            marginBottom: '20px',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div
              className="progress-bar"
              style={{
                width: `${(currentIndex / (questionItems.length - 1)) * 100}%`,
                height: '100%'
              }}
            />
          </div>
        )}

        {/* Current Question */}
        {renderCurrentQuestion()}

        {/* Validation Error */}
        {validationError && (
          <div style={{
            padding: '12px 16px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '10px',
            color: '#dc2626',
            fontSize: '14px',
            fontWeight: '500',
            textAlign: 'center',
            marginTop: '8px',
          }}>
            {validationError}
          </div>
        )}

        {/* Navigation Buttons */}
        {!submitted && !loading && !error && questionItems.length > 0 && renderNavigationButtons()}

        {/* Powered by */}
        {!submitted && (
          <div style={{ textAlign: "center", padding: "20px 0", fontSize: "12px", color: "#bbb" }}>
            Powered by Beu
          </div>
        )}
      </div>
    </div>
  );
};

export default FormFill;
